import React, { useState, useEffect, useCallback } from 'react';
import { Contract, Supervisor, Controller, Customer } from '../types';
import apiService from '../services/api';
import Modal from './Modal';

interface ContractAssignmentProps {
  contract: Contract;
  onUpdate: () => void;
}

interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: Contract;
  supervisors: Supervisor[];
  controllers: Controller[];
  customer: Customer | null;
  onAssign: (supervisorId?: number, controllerId?: number) => Promise<void>;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({
  isOpen,
  onClose,
  contract,
  supervisors,
  controllers,
  customer,
  onAssign
}) => {
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<number | undefined>(
    customer?.supervisor_id
  );
  const [selectedControllerId, setSelectedControllerId] = useState<number | undefined>(
    customer?.controller_id
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen && customer) {
      setSelectedSupervisorId(customer.supervisor_id);
      setSelectedControllerId(customer.controller_id);
      setError(null);
    }
  }, [isOpen, customer]);

  const handleSubmit = async () => {
    setError(null);
    
    // Validate that at least one is selected
    if (!selectedSupervisorId && !selectedControllerId) {
      setError('Please select either a supervisor or a controller');
      return;
    }

    // Validate that only one type is selected
    if (selectedSupervisorId && selectedControllerId) {
      setError('Please select either a supervisor OR a controller, not both');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAssign(selectedSupervisorId, selectedControllerId);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Supervisor or Controller"
    >
      <div className="assignment-modal">
        <div className="modal-info">
          <p><strong>Contract:</strong> {contract.contract_name}</p>
          <p><strong>Customer:</strong> {customer?.customer_name || 'N/A'}</p>
          <p><strong>Current Type:</strong> {customer?.customer_type || 'Not assigned'}</p>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        <div className="form-group">
          <label className="form-label">Assign Supervisor</label>
          <select
            className="form-control"
            value={selectedSupervisorId || ''}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : undefined;
              setSelectedSupervisorId(value);
              if (value) setSelectedControllerId(undefined);
            }}
            disabled={isSubmitting}
          >
            <option value="">-- No Supervisor --</option>
            {supervisors.map((supervisor) => (
              <option key={supervisor.id} value={supervisor.id}>
                {supervisor.display_name || `Supervisor ${supervisor.supervisor_id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="form-divider">OR</div>

        <div className="form-group">
          <label className="form-label">Assign Controller</label>
          <select
            className="form-control"
            value={selectedControllerId || ''}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : undefined;
              setSelectedControllerId(value);
              if (value) setSelectedSupervisorId(undefined);
            }}
            disabled={isSubmitting}
          >
            <option value="">-- No Controller --</option>
            {controllers.map((controller) => (
              <option key={controller.id} value={controller.id}>
                {controller.controller_name}
              </option>
            ))}
          </select>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting || (!selectedSupervisorId && !selectedControllerId)}
          >
            {isSubmitting ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ContractAssignment: React.FC<ContractAssignmentProps> = ({ contract, onUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCustomerData = useCallback(async () => {
    try {
      const customerData = await apiService.getCustomer(contract.customer_id);
      setCustomer(customerData);
    } catch (err) {
      console.error('Failed to load customer:', err);
    }
  }, [contract.customer_id]);

  // Load customer data whenever the contract id changes
  useEffect(() => {
    if (contract.customer_id) {
      loadCustomerData();
    }
  }, [contract.customer_id, loadCustomerData]);

  const loadAssignmentData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load active supervisors and controllers
      const [supervisorsData, controllersData] = await Promise.all([
        apiService.getAllSupervisors(),
        apiService.getAllControllers()
      ]);

      // Filter only active ones
      setSupervisors(supervisorsData.filter((s: Supervisor) => s.is_active));
      setControllers(controllersData.filter((c: Controller) => c.is_active));
    } catch (err) {
      setError('Failed to load assignment data');
      console.error('Error loading assignment data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async () => {
    await loadAssignmentData();
    setIsModalOpen(true);
  };

  const handleAssign = async (supervisorId?: number, controllerId?: number) => {
    if (!customer) {
      throw new Error('No customer found for this contract');
    }

    // Update the customer with new assignment
    const updateData: any = {
      ...customer,
      supervisor_id: supervisorId || null,
      controller_id: controllerId || null,
      customer_type: supervisorId ? 'supervisor' : 'controller'
    };

    await apiService.updateCustomer(customer.id, updateData);
    
    // Reload customer data
    await loadCustomerData();
    
    // Notify parent component
    onUpdate();
  };

  const getAssignmentStatus = () => {
    if (!customer) return { text: 'No customer assigned', className: 'text-muted' };
    
    if (customer.customer_type === 'supervisor' && customer.supervisor_id) {
      const supervisor = supervisors.find(s => s.id === customer.supervisor_id);
      return {
        text: `Supervisor: ${supervisor?.display_name || `ID ${customer.supervisor_id}`}`,
        className: 'text-primary'
      };
    }
    
    if (customer.customer_type === 'controller' && customer.controller_id) {
      const controller = controllers.find((c: Controller) => c.id === customer.controller_id);
      return {
        text: `Controller: ${controller?.controller_name || `ID ${customer.controller_id}`}`,
        className: 'text-success'
      };
    }
    
    return { text: 'Not supervised', className: 'text-warning' };
  };

  const status = getAssignmentStatus();

  return (
    <div className="contract-assignment">
      <div className="assignment-info">
        <span className={`assignment-status ${status.className}`}>
          {status.text}
        </span>
        <button
          className="btn btn-sm btn-outline-primary ml-2"
          onClick={handleOpenModal}
          disabled={!contract.customer_id || loading}
          title={!contract.customer_id ? 'No customer assigned to this contract' : 'Manage assignment'}
        >
          <i className="fas fa-user-tie"></i> Manage Assignment
        </button>
      </div>

      {error && (
        <div className="alert alert-danger mt-2">{error}</div>
      )}

      <AssignmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contract={contract}
        supervisors={supervisors}
        controllers={controllers}
        customer={customer}
        onAssign={handleAssign}
      />
    </div>
  );
};

export default ContractAssignment;